import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme, useMediaQuery } from '@material-ui/core';
import {
  BankAccountView,
  PurchaseInvoiceBaseModel,
  PurchaseInvoicesSummary,
  SortParams,
  SortParamsArg,
} from 'models';
import { formatNum, toNumber } from 'utils';
import { format } from 'date-fns';
import { purchaseInvoicesTableCells } from 'configs';

import { buildInvoiceNavigation } from './helpers';

import PurchaseInvoice from 'features/purchase-invoices/PurchaseInvoice';
import {
  PageLoader,
  Error,
  FiltersBar,
  Table,
  DatePicker,
  TableSearchBar,
  Modal,
} from 'components';
import { Button, Collapse } from '@material-ui/core';
import BankAccountsSelect from './BankAccountsSelect';
import ArrowDown from '@material-ui/icons/KeyboardArrowDown';

import clsx from 'clsx';
import { useStyles } from './style';

interface Props {
  searchTerm: string;
  startDate: Date | null;
  endDate: Date | null;
  sortParams: SortParams;
  displayedBankAccount: BankAccountView | null;
  purchaseInvoices: PurchaseInvoiceBaseModel[];
  summary: PurchaseInvoicesSummary | null;
  loading: boolean;
  error: boolean;
  isLoadingPurchaseInvoicesList: boolean;
  handleChangeSortParams: (params: SortParamsArg) => void;
  handleDateChange: (date: Date | null, name?: string) => void;
  handleChangeSearchTerm: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const PurchaseInvoicesDetails: React.FC<Props> = ({
  endDate,
  error,
  isLoadingPurchaseInvoicesList,
  loading,
  displayedBankAccount,
  purchaseInvoices,
  searchTerm,
  sortParams,
  startDate,
  summary,
  handleDateChange,
  handleChangeSearchTerm,
  handleChangeSortParams,
}) => {
  const theme = useTheme();
  const sm = useMediaQuery(theme.breakpoints.down(theme.size.sm));

  const classes = useStyles();
  const { t } = useTranslation();

  const [openMobileFilter, setOpenMobileFilter] = useState(true);
  const [openMobileSummary, setOpenMobileSummary] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(
    null
  );

  const inputCls = useMemo(
    () => ({
      root: classes.inputWrapper,
    }),
    [classes]
  );

  const handleToggleMobileFilterVisibility = useCallback(() => {
    setOpenMobileFilter((open) => !open);
  }, []);

  const handleToggleMobileSummaryVisibility = useCallback(() => {
    setOpenMobileSummary((open) => !open);
  }, []);

  const renderMobileFilters = () => {
    return (
      <FiltersBar
        className={clsx(classes.topFiltersBar, classes.mobileFiltersBar)}
      >
        <div className={classes.mobileFiltersLeft}>
          <TableSearchBar
            value={searchTerm}
            onChange={handleChangeSearchTerm}
            placeholder={t('#portal.purchaseinvoice.grid.search.tip')}
            cls={inputCls}
          />
        </div>
        <div className={classes.mobileFiltersRight}>
          <BankAccountsSelect />
          <div
            className={clsx(
              classes.flex,
              classes.ml20,
              classes.mobileDatePickerWrapper
            )}
          >
            <DatePicker
              date={startDate}
              name="startDate"
              handleChange={handleDateChange}
              maxDate={endDate}
            />
            <span className={classes.divider}>&mdash;</span>
            <DatePicker
              date={endDate}
              name="endDate"
              handleChange={handleDateChange}
              minDate={startDate}
            />
          </div>
        </div>
      </FiltersBar>
    );
  };

  const handleRowClick = useCallback((invoice: PurchaseInvoiceBaseModel) => {
    setSelectedInvoiceId(invoice.Id);
  }, []);

  const handleCloseModal = useCallback(() => {
    setSelectedInvoiceId(null);
  }, []);

  const invoicesNavigation = useMemo(() => {
    return buildInvoiceNavigation(purchaseInvoices, selectedInvoiceId);
  }, [purchaseInvoices, selectedInvoiceId]);

  const handleSelectPrevInvoice = useCallback(() => {
    if (invoicesNavigation.prev) {
      setSelectedInvoiceId(invoicesNavigation.prev.Id);
    }
  }, [invoicesNavigation.prev]);

  const handleSelectNextInvoice = useCallback(() => {
    if (invoicesNavigation.next) {
      setSelectedInvoiceId(invoicesNavigation.next.Id);
    }
  }, [invoicesNavigation.next]);

  const canSelectNextInvoice = !!invoicesNavigation.next;
  const canSelecPrevInvoice = !!invoicesNavigation.prev;

  if (loading) {
    return <PageLoader />;
  }

  if (error) {
    return <Error />;
  }

  return (
    <div>
      <FiltersBar
        className={classes.topFiltersBar}
        onClick={sm ? handleToggleMobileFilterVisibility : undefined}
      >
        <h2 className={classes.title}>
          {t('#portal.purchaseinvoice.grid.page')}
        </h2>
        {sm ? (
          <Button className={classes.toggleBtn}>
            <ArrowDown
              className={clsx({
                [classes.activeToggleIcon]: openMobileFilter,
              })}
            />
          </Button>
        ) : (
          <div className={classes.flex}>
            <BankAccountsSelect />
            <div className={clsx(classes.flex, classes.ml20)}>
              <DatePicker
                date={startDate}
                name="startDate"
                handleChange={handleDateChange}
                maxDate={endDate}
              />
              <span className={classes.divider}>&mdash;</span>
              <DatePicker
                date={endDate}
                name="endDate"
                handleChange={handleDateChange}
                minDate={startDate}
              />
            </div>
          </div>
        )}
      </FiltersBar>
      {sm ? (
        <Collapse in={openMobileFilter} unmountOnExit>
          {renderMobileFilters()}
        </Collapse>
      ) : null}
      {sm ? (
        <div
          className={classes.mobileSummaryTopBar}
          onClick={handleToggleMobileSummaryVisibility}
        >
          <p className={classes.mobileSummaryTitle}>Yhteenveto</p>
          <Button
            className={clsx(classes.toggleBtn, classes.mobileSummaryToggleBtn)}
          >
            <ArrowDown
              className={clsx({
                [classes.activeToggleIcon]: openMobileSummary,
              })}
            />
          </Button>
        </div>
      ) : null}
      <Collapse in={sm ? openMobileSummary : true} unmountOnExit>
        <ul className={classes.summaryList}>
          <li className={classes.summaryItem}>
            <span className={classes.summaryLabel}>
              {t('#portal.purchaseinvoice.grid.info.invoices')}
            </span>
            <span className={classes.value}>{summary?.InvoicesCount}</span>
          </li>

          <li className={classes.summaryItem}>
            <span className={classes.summaryLabel}>
              {t('#portal.purchaseinvoice.grid.info.totalamountofinvoices')}
            </span>
            <span className={classes.value}>
              {formatNum(summary?.TotalAmountOfInvoices?.Value)}{' '}
              {summary?.TotalAmountOfInvoices?.Symbol}
            </span>
          </li>

          <li className={classes.summaryItem}>
            <span className={classes.summaryLabel}>
              {t('#portal.purchaseinvoice.grid.info.totalamountofpaidinvoices')}
            </span>
            <span
              className={clsx(classes.value, {
                [classes.green]:
                  toNumber(summary?.TotalAmountOfPaidInvoices?.Value) > 0,
              })}
            >
              {formatNum(summary?.TotalAmountOfPaidInvoices?.Value)}{' '}
              {summary?.TotalAmountOfPaidInvoices?.Symbol}
            </span>
          </li>

          <li className={classes.summaryItem}>
            <span className={classes.summaryLabel}>
              {t(
                '#portal.purchaseinvoice.grid.info.totalamountofunpaidinvoices'
              )}
            </span>
            <span
              className={clsx(classes.value, {
                [classes.orange]:
                  toNumber(summary?.TotalAmountOfUnpaidInvoices?.Value) > 0,
              })}
            >
              {formatNum(summary?.TotalAmountOfUnpaidInvoices?.Value)}{' '}
              {summary?.TotalAmountOfUnpaidInvoices?.Symbol}
            </span>
          </li>

          <li className={classes.summaryItem}>
            <span className={classes.summaryLabel}>
              {t('#portal.purchaseinvoice.grid.info.balanceofbankaccount')}{' '}
              {displayedBankAccount?.BalanceDate
                ? format(
                    new Date(displayedBankAccount?.BalanceDate),
                    'd.M.yyyy'
                  )
                : null}
            </span>
            <span className={classes.value}>
              {displayedBankAccount?.Balance
                ? `${formatNum(displayedBankAccount.Balance.Value)} ${
                    displayedBankAccount.Balance.Symbol
                  }`
                : null}
            </span>
          </li>
        </ul>
      </Collapse>

      {!sm && (
        <FiltersBar className={classes.flexBetween}>
          <h2 className={classes.title}>
            {t('#portal.purchaseinvoice.grid.label')}
          </h2>
          <TableSearchBar
            value={searchTerm}
            onChange={handleChangeSearchTerm}
            placeholder={t('#portal.purchaseinvoice.grid.search.tip')}
            cls={inputCls}
          />
        </FiltersBar>
      )}
      <Table
        list={purchaseInvoices}
        loading={isLoadingPurchaseInvoicesList}
        tableCells={purchaseInvoicesTableCells}
        order={sortParams.order}
        orderBy={sortParams.orderBy}
        handleChangeSortParams={handleChangeSortParams}
        handleRowClick={handleRowClick}
      />
      <Modal
        open={!!selectedInvoiceId}
        handleClose={handleCloseModal}
        maxWidth="lg"
        fullWidth
        fullHeight
        cls={{ childrenRoot: classes.modalBackground }}
      >
        <PurchaseInvoice
          canSelectNextInvoice={canSelectNextInvoice}
          canSelectPrevInvoice={canSelecPrevInvoice}
          invoiceId={selectedInvoiceId}
          onSelectNextInvoice={handleSelectNextInvoice}
          onSelectPrevInvoice={handleSelectPrevInvoice}
        />
      </Modal>
    </div>
  );
};

export default PurchaseInvoicesDetails;
